<template>
  <div class="tab-body w-fixed-s calidad-pagos">

    <div class="fixed-section">

      <Form :validation-schema="schema" :initial-values="formValues">

        <div class="action-box justify-content-start">
          <div class="grid grid-container-1">
            <div class="grid-item-1">
              <SelectButton
                name="anio"
                label="año"
                v-model="model.anio"
                @change="cargar()"
                :options="yearsOptions"
              />
            </div>
          </div>
        </div>

      </Form>

    </div>

    <div class="scrollable-content">

      <h3 class="title-acceso-ganaderos mb-4">Gráfica de {{ titulo }} por quincena</h3>

      <GradientLineChart
        :id="id"
        :title="titulo"
        :yAxisTicksDecimals="true"
        :decimals="decimales"
        :enlarge="true"
        @open-modal="mostrarModal()"
        description=""
        :chart="{
          labels: labels,
          yAxisTitle: unidad,
          suggestedMin: sugeridoMin,
          suggestedMax: sugeridoMax,
          datasets: datasets
        }"
      />

      <h3 class="title-acceso-ganaderos mt-5 mb-4">Detalle de {{ titulo }} por quincena</h3>

      <!-- Comparativo interanual -->
      <template v-if="esComparativo">

        <div class="b-table blue acceso-ganaderos-acopio" v-if="yearsOptions.length > 0">

          <div class="t-header">
            <div class="t-row">
              <div class="t-cell">quincena</div>
              <div class="t-cell">{{ yearsOptions[3].id }}</div>
              <div class="t-cell">{{ yearsOptions[2].id }}</div>
              <div class="t-cell">{{ yearsOptions[1].id }}</div>
              <div class="t-cell"></div>
            </div>
          </div>

          <div class="t-body">
            <div class="t-row" v-for="i, index in tablaComparativa" :key="index">
              <div class="t-cell"><span class="mobile-cell-title">quincena</span> {{ i.half + "Q " + $filters.getMonthAbr(i.month) }} </div>
              <div class="t-cell"><span class="mobile-cell-title">{{ yearsOptions[3].id }}</span>{{ formatear(i.col1) }}</div>
              <div class="t-cell"><span class="mobile-cell-title">{{ yearsOptions[2].id }}</span>{{ formatear(i.col2) }}</div>
              <div class="t-cell"><span class="mobile-cell-title">{{ yearsOptions[1].id }}</span>{{ formatear(i.col3) }}</div>
              <div class="t-cell action"></div>
            </div>
          </div>

          <div class="t-body" v-if="tablaComparativa.length == 0">
            <div class="t-body no-records">
              <p>No existe data para esta fecha</p>
            </div>
          </div>

        </div>

      </template>

      <!-- Detalle de un año -->
      <template v-else>

        <div class="row one-mobile-table mb-5">

          <div class="col-md-4" v-for="bloque, indice in bloques" :key="indice">

            <div class="b-table blue mt-5 lista-calidad">

              <div class="t-header">
                <div class="t-row">
                  <div class="t-cell">quincena</div>
                  <div class="t-cell" v-html="etiquetaColumna"></div>
                </div>
              </div>

              <div class="t-body">
                <div class="t-row" v-for="registro, i in bloque" :key="i">
                  <div class="t-cell"><span class="mobile-cell-title">quincena</span>{{ $filters.getPeriod(registro.quincena, registro.mes) }}</div>
                  <div class="t-cell"><span class="mobile-cell-title" v-html="etiquetaColumna"></span>{{ formatear(registro.valor) }}</div>
                </div>
              </div>

            </div>

          </div>

        </div>

        <div class="b-table blue" v-if="sinDatos">
          <div class="t-body no-records">
            <p>No existe data para esta fecha</p>
          </div>
        </div>

      </template>

      <div class="form-actions mb-5">
        <Button
          class="mt-3 primary-button shadow-none"
          size="lg"
          type="button"
          @click="exportar()"
          >Exportar</Button
        >
      </div>

    </div>

    <transition name="fade">
      <CustomModal
        :show="modalVisible"
        :isHeader="false"
        :isFooter="false"
        :width="1600"
        title=""
        v-on:modal:close="ocultarModal()"
      >
        <template v-slot:body>

          <GradientLineChart
            :id="`${id}_modal`"
            :title="titulo"
            :yAxisTicksDecimals="true"
            :decimals="decimales"
            :enlarge="false"
            description=""
            :chart="{
              labels: labels,
              yAxisTitle: unidad,
              suggestedMin: sugeridoMin,
              suggestedMax: sugeridoMax,
              datasets: datasets
            }"
          />

          <div class="form-actions">
            <Button
              class="mt-3 ghost-button shadow-none"
              size="lg"
              type="button"
              @click="ocultarModal()"
              >Cerrar</Button
            >
          </div>

        </template>
      </CustomModal>
    </transition>

  </div>
</template>

<script>
import { markRaw } from "vue";
import { Form } from "vee-validate";
import * as Yup from "yup";
import GradientLineChart from "@/components/Charts/GradientLineChart.vue";
import SelectButton from "@/components/Inputs/SelectButton";
import Button from "@/components/Inputs/Button";
import CustomModal from "@/components/CustomModal.vue";

const CACHE_NAME = "site-cache-v2";
const COLORES_SERIE = ["#F78D2C", "#1D4193", "#07C25E"];
const REGISTROS_POR_BLOQUE = 8;

export default {
  name: "IndicadorCalidad",
  components: { Form, GradientLineChart, SelectButton, Button, CustomModal },
  props: {
    // Identificador del lienzo de la gráfica. Debe ser único dentro de la vista.
    id: { type: String, required: true },
    // Nombre visible del indicador (Grasa, Proteína, Úrea, NUL).
    titulo: { type: String, required: true },
    // Unidad mostrada en el eje Y (% o mg/dL).
    unidad: { type: String, default: "" },
    // Encabezado de la columna de valores en las tablas de detalle. Admite <br>.
    etiquetaColumna: { type: String, required: true },
    // Cantidad de decimales del indicador.
    decimales: { type: Number, default: 2 },
    // Rango de referencia del eje Y, documentado a partir del histórico.
    // ATENCIÓN: hoy GradientLineChart NO lee estas propiedades. Calcula la escala
    // desde los propios datos con un margen fijo, y las únicas propiedades que
    // consulta se llaman chart.min y chart.max. Se conservan para no perder el
    // rango observado si algún día la gráfica pasa a respetar un rango fijo.
    sugeridoMin: { type: Number, required: true },
    sugeridoMax: { type: Number, required: true },
    // Opciones y etiquetas provistas por la vista contenedora.
    yearsOptions: { type: Array, required: true },
    yearsServer: { type: [String, Array], required: true },
    labels: { type: Array, required: true },
    // Clave del caché offline. Debe ser distinta por indicador.
    claveCache: { type: String, required: true },
    // Acciones Vuex de consulta y exportación.
    accionLista: { type: String, required: true },
    accionDescarga: { type: String, required: true },
    // Prefijo del archivo exportado.
    nombreArchivo: { type: String, required: true },
  },
  data() {
    return {
      schema: markRaw(Yup.object().shape({})),
      formValues: { anio: "" },
      model: { anio: "" },
      dataRaw: [],
      datasets: [],
      tablaComparativa: [],
      bloques: [],
      modalVisible: false,
    };
  },
  computed: {
    esComparativo() {
      return this.model.anio == 0;
    },
    sinDatos() {
      return !this.esComparativo && this.bloques.length == 0;
    },
  },
  watch: {
    // Este componente se monta antes que la vista contenedora, que resuelve
    // yearsOptions y yearsServer en su propio mounted. Por eso la inicialización
    // se dispara cuando las opciones llegan y no en el mounted local: de lo
    // contrario el selector queda sin valor y muestra el texto de marcador.
    yearsOptions: {
      immediate: true,
      handler(opciones) {
        if (!opciones || opciones.length === 0 || this.model.anio !== "") {
          return;
        }

        this.model.anio = opciones[0].id;
        this.formValues.anio = opciones[0].id;
        this.cargar();
      },
    },
  },
  methods: {
    formatear(valor) {
      if (valor === null || valor === undefined || valor === "") {
        return "-";
      }
      return this.decimales === 3
        ? this.$filters.roundToThree(valor)
        : this.$filters.roundToTwo(valor);
    },
    mostrarModal() {
      this.modalVisible = true;
    },
    ocultarModal() {
      this.modalVisible = false;
    },
    cargar() {
      // Lectura optimista del caché offline para que la pestaña responda sin conexión.
      caches
        .open(CACHE_NAME)
        .then((cache) => {
          cache
            .match(`/offline/${this.claveCache}`)
            .then((response) => (response ? response.json() : null))
            .then((data) => {
              if (data) {
                this.dataRaw = data;
                this.procesar();
              }
            });
        })
        .catch(() => {});

      this.$store
        .dispatch(this.accionLista, { years: this.yearsServer })
        .then((response) => {
          if (response.status == 200) {
            caches
              .open(CACHE_NAME)
              .then((cache) => {
                cache.put(
                  `/offline/${this.claveCache}`,
                  new Response(JSON.stringify(response.data))
                );
              })
              .catch(() => {});

            this.dataRaw = response.data;
            this.procesar();
          }
        })
        .catch(() => {});
    },
    procesar() {
      if (this.esComparativo) {
        this.tablaComparativa = this.construirTablaComparativa(this.dataRaw);

        const anios = this.yearsOptions.filter((opcion) => opcion.id !== 0);

        this.datasets = anios.map((opcion, indice) => {
          const serie = this.$filters.generarSecuenciaQuincenas(
            this.$filters.getSolidosTotalesByYear(this.dataRaw, opcion.id, "valor", "numeroQuincena"),
            opcion.id,
            "valor",
            "numeroQuincena"
          );

          return {
            legendTitle: opcion.id,
            data: serie.map((punto) => punto.valor),
            borderColor: COLORES_SERIE[indice] || COLORES_SERIE[0],
          };
        });

        this.bloques = [];
        return;
      }

      const anio = this.model.anio;

      const registrosAnio = this.$filters.getSolidosTotalesByYear(this.dataRaw, anio, "valor", "numeroQuincena");
      const serie = this.$filters.generarSecuenciaQuincenas(registrosAnio, anio, "valor", "numeroQuincena");

      this.datasets = [
        {
          legendTitle: anio,
          data: serie.map((punto) => punto.valor),
          borderColor: COLORES_SERIE[0],
        },
      ];

      const detalle = this.$filters.getDataByPeriodsV2(registrosAnio, anio);

      const bloques = [];
      for (let i = 0; i < detalle.length; i += REGISTROS_POR_BLOQUE) {
        bloques.push(detalle.slice(i, i + REGISTROS_POR_BLOQUE));
      }
      this.bloques = bloques;
      this.tablaComparativa = [];
    },
    construirTablaComparativa(datos) {
      const anioMaximo = new Date().getFullYear();
      const anioMinimo = anioMaximo - 2;

      const porAnio = {};
      for (let anio = anioMinimo; anio <= anioMaximo; anio++) {
        porAnio[anio] = buscarPorAnio(anio, datos);
      }

      const filas = [];
      for (let mes = 1; mes <= 12; mes++) {
        for (let quincena = 1; quincena <= 2; quincena++) {
          filas.push({
            half: quincena,
            month: mes,
            col1: valorDeQuincena(quincena, mes, porAnio[anioMinimo]),
            col2: valorDeQuincena(quincena, mes, porAnio[anioMinimo + 1]),
            col3: valorDeQuincena(quincena, mes, porAnio[anioMaximo]),
          });
        }
      }

      function buscarPorAnio(anio, datos) {
        for (const clave in datos) {
          if (Object.prototype.hasOwnProperty.call(datos, clave) && datos[clave].year === anio) {
            return datos[clave].data;
          }
        }
        return [];
      }

      function valorDeQuincena(quincena, mes, registros) {
        if (!registros || registros.length === 0) {
          return "";
        }
        for (let i = 0; i < registros.length; i++) {
          if (registros[i].mes == mes && registros[i].numeroQuincena == quincena) {
            return registros[i].valor;
          }
        }
        return "";
      }

      return filas.sort((a, b) => a.month - b.month || a.half - b.half);
    },
    exportar() {
      const data = {
        years: this.esComparativo ? this.yearsServer : this.model.anio,
      };

      this.$store.dispatch(this.accionDescarga, data).then((response) => {
        if (response.status != 200) {
          return;
        }

        const archivo = new Blob([response.data], { type: response.headers["content-type"] });
        const url = URL.createObjectURL(archivo);
        const enlace = document.createElement("a");

        enlace.href = url;
        enlace.setAttribute("download", `${this.nombreArchivo}_${this.$filters.generateFileDate()}.xlsx`);
        document.body.appendChild(enlace);
        enlace.click();
        document.body.removeChild(enlace);
        URL.revokeObjectURL(url);
      });
    },
  },
};
</script>
